import React, { useState } from "react"
import {
  fetchUrlUnfurl,
  UrlUnfurlResponse,
} from "../../util/backend_api/urlUnfurl"
import { FeedSpinner } from "../../components/shared/Spinner"
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs, { extend } from "dayjs"
import customParseFormat from "dayjs/plugin/customParseFormat"
import { createEvent, updateEvent } from "../../util/backend_api/posts"
import { useQueryClient } from "react-query"
import { toast } from "react-toastify"
import { BAND_POSTS_QUERY_KEY } from "../../hooks/useBandPosts"

extend(customParseFormat)

export interface ExternalEventFormProps {
  bandId: number
  onClose: () => void
  postId?: number
  initialValues?: EventFormValues
}

type View = "form" | "loading" | "result" | "error"

export interface EventFormValues {
  url: string
  title: string
  location: string
  date: string
  image: string
  ticket_price: string
}

const emptyFormValues: EventFormValues = {
  url: "",
  title: "",
  location: "",
  date: "",
  image: "",
  ticket_price: "",
}

/** Parse title + description for price, date, and location (e.g. DICE-style "| $13.60 | 7 Mar @ Venue, City |") */
function parseEventFromText(
  title: string,
  description: string,
): Pick<EventFormValues, "ticket_price" | "date" | "location"> {
  const text = [title, description].filter(Boolean).join(" ")
  const result: Pick<EventFormValues, "ticket_price" | "date" | "location"> = {
    ticket_price: "",
    date: "",
    location: "",
  }

  // Price: $13.60 or $13 or "Free"
  const priceMatch = text.match(/\$[\d,.]+/)
  if (priceMatch) {
    result.ticket_price = priceMatch[0]
  } else if (/\bFree\b/i.test(text)) {
    result.ticket_price = "Free"
  }

  // Location: after @ until | or end (e.g. "7 Mar @ The Spare Room at The Gutter, Brooklyn |")
  const atMatch = text.match(/@\s*([^|]+?)(?:\s*\||$)/s)
  if (atMatch) {
    result.location = atMatch[1].trim()
  }

  // Date: "7 Mar", "Mar 7", "March 7", "7 March", "Mar 7th", "12/25", "2025-03-07" etc.
  const year = dayjs().year()
  const dateFormats = [
    "D MMM YYYY",
    "D MMM",
    "MMM D YYYY",
    "MMM D",
    "MMMM D YYYY",
    "MMMM D",
    "D MMMM YYYY",
    "D MMMM",
    "MM/DD/YYYY",
    "YYYY-MM-DD",
    "M/D/YYYY",
    "D/M/YYYY",
  ]
  // Try to find a date-like segment (day + month name or numbers)
  const dateSegmentMatch = text.match(
    /(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\s+\d{1,2}|\d{4}-\d{2}-\d{2})/i,
  )
  if (dateSegmentMatch) {
    let dateStr = dateSegmentMatch[1].trim()
    if (/^\d{1,2}\s+[A-Za-z]+$/.test(dateStr)) {
      dateStr = `${dateStr} ${year}`
    } else if (/^[A-Za-z]+\s+\d{1,2}$/.test(dateStr)) {
      dateStr = `${dateStr} ${year}`
    }
    for (const fmt of dateFormats) {
      const d = dayjs(dateStr, fmt, true)
      if (d.isValid()) {
        result.date = d.toISOString()
        break
      }
    }
  }

  return result
}

function stripImgixParams(url: string): string {
  try {
    const parsed = new URL(url)
    if (parsed.hostname.includes("imgix.net")) {
      return `${parsed.origin}${parsed.pathname}`
    }
  } catch {
    // not a valid URL, return as-is
  }
  return url
}

function formValuesFromLinkMeta(
  linkMeta: UrlUnfurlResponse["link_meta"],
): EventFormValues {
  const parsed = parseEventFromText(
    linkMeta.best_title || "",
    linkMeta.description || "",
  )
  return {
    url: linkMeta.url || "",
    title: linkMeta.best_title || "",
    location: parsed.location,
    date: parsed.date,
    image: linkMeta.best_image ? stripImgixParams(linkMeta.best_image) : "",
    ticket_price: parsed.ticket_price,
  }
}

export const ExternalEventForm: React.FC<ExternalEventFormProps> = ({
  bandId,
  onClose,
  postId,
  initialValues,
}) => {
  const isEditing = postId != null
  const queryClient = useQueryClient()
  const [url, setUrl] = useState(initialValues?.url ?? "")
  const [view, setView] = useState<View>(initialValues ? "result" : "form")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [formValues, setFormValues] = useState<EventFormValues>(
    initialValues ?? emptyFormValues,
  )
  const [isSaving, setIsSaving] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof EventFormValues, string>>
  >({})

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const trimmed = url.trim()
    if (!trimmed) return
    setView("loading")
    setErrorMessage(null)
    try {
      const response = await fetchUrlUnfurl(trimmed)
      setFormValues(formValuesFromLinkMeta(response.link_meta))
      setSaveError(null)
      setView("result")
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : "Failed to load URL")
      setView("error")
    }
  }

  const handleRefresh = async () => {
    const fetchUrl = formValues.url?.trim()
    if (!fetchUrl) return
    setIsRefreshing(true)
    setErrorMessage(null)
    try {
      const response = await fetchUrlUnfurl(fetchUrl)
      setFormValues(formValuesFromLinkMeta(response.link_meta))
      setSaveError(null)
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to refresh metadata",
      )
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleClose = () => {
    setUrl("")
    setView("form")
    setFormValues(emptyFormValues)
    setErrorMessage(null)
    setFieldErrors({})
    onClose()
  }

  const updateField = (field: keyof EventFormValues, value: string) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const getRequiredErrors = (): Partial<
    Record<keyof EventFormValues, string>
  > => {
    const err: Partial<Record<keyof EventFormValues, string>> = {}
    if (!formValues.url?.trim()) err.url = "Event URL is required"
    if (!formValues.title?.trim()) err.title = "Title is required"
    if (!formValues.location?.trim()) err.location = "Location is required"
    if (!formValues.date?.trim()) err.date = "Event date is required"
    if (!formValues.ticket_price?.trim())
      err.ticket_price = "Ticket price is required"
    return err
  }

  const handleSave = async () => {
    const errors = getRequiredErrors()
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors)
      setSaveError("Please fill in all required fields.")
      return
    }
    setFieldErrors({})
    setIsSaving(true)
    setSaveError(null)
    const publishedAt = formValues.date || null
    const now = new Date().toISOString()
    const postHash = {
      post_type: "external_event_url",
      title: formValues.title.trim(),
      published_at: publishedAt,
      active_date: publishedAt,
      announced_at: now,
      status: "published",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      external_url: formValues.url?.trim() || null,
      external_photo_src: formValues.image?.trim() || null,
      content_hash: {
        ...(formValues.location?.trim() && {
          location: formValues.location.trim(),
        }),
        ...(formValues.ticket_price?.trim() && {
          ticket_price: formValues.ticket_price.trim(),
        }),
      },
    }
    try {
      const response = isEditing
        ? await updateEvent(bandId, postId, postHash, [], false)
        : await createEvent(bandId, postHash, [], false)
      if (response?.error) {
        setSaveError(
          response.error?.message ||
            response.error?.toString() ||
            "Failed to save",
        )
        return
      }
      if (response?.success === false) {
        const errMsg =
          response?.errors && typeof response.errors === "object"
            ? Object.values(response.errors).flat().join(", ")
            : "Validation failed"
        setSaveError(errMsg)
        return
      }
      queryClient.invalidateQueries({
        queryKey: [BAND_POSTS_QUERY_KEY, bandId],
      })
      toast.success(
        isEditing ? "External Event updated" : "Your External Event was added",
      )
      handleClose()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "Failed to save")
    } finally {
      setIsSaving(false)
    }
  }

  const resultTitle = isEditing ? "Edit External Event" : "Add External Event"

  return (
    <div className="space-y-4">
      {view === "form" && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <h2
            id="external-event-form-title"
            className="text-xl font-bold text-gray-900 mb-3"
          >
            Add External Event
          </h2>
          <div className="mb-6">
            <label
              htmlFor="external-event-url"
              className="block font-bold text-gray-700 text-xs uppercase"
            >
              Event URL
            </label>
            <input
              id="external-event-url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://..."
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none text-sm"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 focus:outline-none"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!url.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none disabled:opacity-50"
            >
              Fetch
            </button>
          </div>
          <p className="italic mt-4 text-gray-700 text-sm">
            External events will not be displayed on your band page, but will
            show up in your dashboard, and in your embeeded concert feed.
          </p>
        </form>
      )}

      {view === "loading" && (
        <div className="py-8 flex flex-col items-center justify-center gap-4">
          <FeedSpinner />
          <p className="text-sm text-gray-600">Loading event details…</p>
        </div>
      )}

      {view === "error" && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900">{resultTitle}</h2>
          <div className="rounded bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
            {errorMessage}
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setView(isEditing ? "result" : "form")
                setErrorMessage(null)
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
            >
              {isEditing ? "Back" : "Try again"}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {view === "result" && (
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-gray-900">{resultTitle}</h2>

          <div className="gap-6 grid grid-cols-1 lg:grid-cols-2">
            {formValues.image ? (
              <div className="rounded overflow-hidden border border-gray-200 bg-gray-100 sm:sticky sm:top-0">
                <img
                  src={formValues.image}
                  alt={formValues.title || "Event"}
                  className="w-full aspect-video object-cover"
                />
              </div>
            ) : (
              <div className="rounded border border-dashed border-gray-300 bg-gray-50 aspect-video flex items-center justify-center text-gray-400 text-sm">
                No image
              </div>
            )}
            <form
              className="space-y-4 min-w-0"
              onSubmit={(e) => {
                e.preventDefault()
                handleSave()
              }}
            >
              <div className="mb-3">
                <label
                  htmlFor="external-event-field-url"
                  className="block font-bold text-gray-700 text-xs uppercase"
                >
                  URL <span className="text-red-500">*</span>
                </label>
                <input
                  id="external-event-field-url"
                  type="url"
                  value={formValues.url}
                  onChange={(e) => updateField("url", e.target.value)}
                  placeholder="https://..."
                  className={`w-full px-4 py-2 border rounded focus:outline-none text-sm ${fieldErrors.url ? "border-red-500" : "border-gray-300"}`}
                />
                {fieldErrors.url ? (
                  <p className="mt-1 text-xs text-red-600">{fieldErrors.url}</p>
                ) : null}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="external-event-field-title"
                  className="block font-bold text-gray-700 text-xs uppercase"
                >
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="external-event-field-title"
                  type="text"
                  value={formValues.title}
                  onChange={(e) => updateField("title", e.target.value)}
                  placeholder="Event title"
                  className={`w-full px-4 py-2 border rounded focus:outline-none text-sm ${fieldErrors.title ? "border-red-500" : "border-gray-300"}`}
                />
                {fieldErrors.title ? (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.title}
                  </p>
                ) : null}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="external-event-field-location"
                  className="block font-bold text-gray-700 text-xs uppercase"
                >
                  Location <span className="text-red-500">*</span>
                </label>
                <input
                  id="external-event-field-location"
                  type="text"
                  value={formValues.location}
                  onChange={(e) => updateField("location", e.target.value)}
                  placeholder="Venue or address"
                  className={`w-full px-4 py-2 border rounded focus:outline-none text-sm ${fieldErrors.location ? "border-red-500" : "border-gray-300"}`}
                />
                {fieldErrors.location ? (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.location}
                  </p>
                ) : null}
              </div>
              <div className="mb-3">
                <label
                  htmlFor="external-event-field-date"
                  className="block font-bold text-gray-700 text-xs uppercase"
                >
                  Event Date <span className="text-red-500">*</span>
                </label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={formValues.date ? dayjs(formValues.date) : null}
                    onChange={(date) =>
                      updateField("date", date?.toISOString() ?? "")
                    }
                    format="MMMM D, YYYY h:mm A"
                    slotProps={{
                      textField: {
                        id: "external-event-field-date",
                        size: "small",
                        fullWidth: true,
                        error: !!fieldErrors.date,
                        helperText: fieldErrors.date,
                        sx: {
                          "& .MuiInputBase-root": {
                            fontSize: "0.875rem",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </div>
              <div className="mb-3">
                <label
                  htmlFor="external-event-field-image"
                  className="block font-bold text-gray-700 text-xs uppercase"
                >
                  Image URL{" "}
                  <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="external-event-field-image"
                  type="url"
                  value={formValues.image}
                  onChange={(e) => updateField("image", e.target.value)}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none text-sm"
                />
              </div>
              <div className="mb-3">
                <label
                  htmlFor="external-event-field-ticket-price"
                  className="block font-bold text-gray-700 text-xs uppercase"
                >
                  Ticket price <span className="text-red-500">*</span>
                </label>
                <input
                  id="external-event-field-ticket-price"
                  type="text"
                  value={formValues.ticket_price}
                  onChange={(e) => updateField("ticket_price", e.target.value)}
                  placeholder="e.g. 15 or Free"
                  className={`w-full px-4 py-2 border rounded focus:outline-none text-sm ${fieldErrors.ticket_price ? "border-red-500" : "border-gray-300"}`}
                />
                {fieldErrors.ticket_price ? (
                  <p className="mt-1 text-xs text-red-600">
                    {fieldErrors.ticket_price}
                  </p>
                ) : null}
              </div>
              {saveError ? (
                <div className="rounded bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800 mb-3">
                  {saveError}
                </div>
              ) : null}
              <div className="flex justify-between items-center pt-2">
                <button
                  type="button"
                  onClick={handleRefresh}
                  disabled={isSaving || isRefreshing || !formValues.url?.trim()}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 focus:outline-none disabled:opacity-50"
                >
                  {isRefreshing ? "Refreshing…" : "Refresh Details"}
                </button>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 focus:outline-none disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving || isRefreshing}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none disabled:opacity-50"
                  >
                    {isSaving
                      ? "Saving…"
                      : isEditing
                        ? "Save Changes"
                        : "Save External Event"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
